import { useEffect, useRef, useState } from 'react';
import { detectEUUser } from '../utils/euDetection';
import { useGoogleCmp, setSiteConsentOpen } from '../utils/googleCmp';
import { isAnalyticsAllowed } from '../analytics';
import {
  clearConsentModalSnooze,
  CONSENT_MODAL_OPEN_EVENT,
  CONSENT_PREFERENCES_UPDATED_EVENT,
  ensureConsentPreferences,
  getConsentPreferences,
  setConsentPreferences,
  shouldShowConsentModalForEuUser,
} from '../utils/consentPreferences';

export default function useSiteConsentFlow({ isAdminPortal, setAnalyticsConsent }) {
  const cmp = useGoogleCmp();
  const [detectedEU, setDetectedEU] = useState(null);
  const [showConsentBanner, setShowConsentBanner] = useState(false);
  const [canDisplay, setCanDisplay] = useState(false);
  const appliedRegion = useRef(null);
  const isEUUser = cmp.gdprApplies === true ? true : detectedEU;

  useEffect(() => {
    if (isAdminPortal || cmp.gdprApplies === true || cmp.isBusy) return undefined;
    let cancelled = false;
    detectEUUser().then((eu) => {
      if (!cancelled) setDetectedEU(eu);
    });
    return () => { cancelled = true; };
  }, [cmp.gdprApplies, cmp.isBusy, isAdminPortal]);

  useEffect(() => {
    if (isAdminPortal) {
      setShowConsentBanner(false);
      setAnalyticsConsent(false);
      return;
    }
    if (isEUUser === null) return;
    if (isEUUser) {
      if (appliedRegion.current === 'eu') return;
      appliedRegion.current = 'eu';
      const preferences = ensureConsentPreferences({ region: 'eu' });
      setShowConsentBanner(shouldShowConsentModalForEuUser());
      setAnalyticsConsent(preferences?.categories?.analytics === true);
    } else if (cmp.status === 'not_required' && appliedRegion.current !== 'non_eu') {
      appliedRegion.current = 'non_eu';
      const localPreferences = getConsentPreferences();
      const preferences = setConsentPreferences({
        region: 'non_eu',
        categories: {
          necessary: true,
          functional: true,
          analytics: true,
          marketing: localPreferences?.categories?.marketing === true,
        },
        decidedAt: new Date().toISOString(),
        source: localPreferences ? 'server_sync' : 'migration',
      });
      setShowConsentBanner(false);
      setAnalyticsConsent(preferences ? preferences.categories.analytics : true);
    }
    // Unavailable CMP + non-EU fallback never grants optional categories.
  }, [cmp.status, isEUUser, isAdminPortal, setAnalyticsConsent]);

  useEffect(() => {
    if (isAdminPortal) return undefined;
    const open = () => {
      clearConsentModalSnooze();
      setShowConsentBanner(true);
    };
    const updated = () => {
      setAnalyticsConsent(isAnalyticsAllowed());
      if (isEUUser === true) setShowConsentBanner(shouldShowConsentModalForEuUser());
    };
    window.addEventListener(CONSENT_MODAL_OPEN_EVENT, open);
    window.addEventListener(CONSENT_PREFERENCES_UPDATED_EVENT, updated);
    return () => {
      window.removeEventListener(CONSENT_MODAL_OPEN_EVENT, open);
      window.removeEventListener(CONSENT_PREFERENCES_UPDATED_EVENT, updated);
    };
  }, [isAdminPortal, isEUUser, setAnalyticsConsent]);

  useEffect(() => {
    setCanDisplay(false);
    if (cmp.isBusy || isAdminPortal) return undefined;
    const frame = window.requestAnimationFrame(() => setCanDisplay(true));
    return () => window.cancelAnimationFrame(frame);
  }, [cmp.isBusy, isAdminPortal]);

  const consentHidden = cmp.isBusy || !canDisplay;
  useEffect(() => {
    setSiteConsentOpen(showConsentBanner);
    return () => setSiteConsentOpen(false);
  }, [showConsentBanner]);

  return {
    isEUUser, showConsentBanner, setShowConsentBanner, consentHidden,
    consentBusy: cmp.isBusy || showConsentBanner,
  };
}
